import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import {
  actionsWithContext, resurfacingCandidates, projectRefKey, normaliseProjectRef,
} from '../../lib/projectDebriefs';

const FONT = "font-['Metropolis']";

// Open actions from EARLIER debriefs that plausibly apply to this project
// (project-debriefs §5).
//
// The whole value of a debrief is that it changes what happens next time, and
// "next time" is the moment someone starts the next job for the same client —
// not a reminder three months later. So this appears where a project is being
// set up, reads what previous debriefs decided, and says so.
//
// Rules that matter:
//   SUGGEST, NEVER BLOCK. Nothing here gates anything. It's a prompt.
//   Dismissal is per PROJECT and recorded (dismissed_on_project_refs), so
//     saying "not relevant here" doesn't silence the action everywhere — it
//     stays open, and still surfaces on the next project.
//   Never resurfaces an action onto the project it came from.
//
// Matching is by client or by topic title (there is no client_id to join on —
// the FS number is the project, so client is a string off the brief).
export default function ResurfacedActions({ projectRef, client, topicTitles = [], className = '' }) {
  const [candidates, setCandidates] = useState([]);
  const [dismissing, setDismissing] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    const ref = normaliseProjectRef(projectRef);
    if (!ref) { setCandidates([]); return; }
    const [{ data: debriefs }, { data: topics }, { data: notes }, { data: actions }] = await Promise.all([
      // Only PUBLISHED debriefs resurface. A draft is someone's working copy —
      // half-written actions shouldn't start appearing on other projects.
      supabase.from('project_debriefs').select('id, project_ref, project_name, client, debrief_date, status').eq('status', 'published'),
      supabase.from('debrief_topics').select('id, debrief_id, title'),
      supabase.from('debrief_notes').select('id, topic_id, body'),
      supabase.from('debrief_actions').select('*').eq('status', 'open'),
    ]);
    const joined = actionsWithContext({
      debriefs: debriefs || [], topics: topics || [], notes: notes || [], actions: actions || [],
    });
    setCandidates(resurfacingCandidates(joined, { projectRef: ref, client, topicTitles }));
  }, [projectRef, client, topicTitles]);

  useEffect(() => { load(); }, [load]);

  const dismiss = async (action) => {
    setDismissing(action.id);
    const refKey = projectRefKey(projectRef);
    const existing = Array.isArray(action.dismissed_on_project_refs) ? action.dismissed_on_project_refs : [];
    // Store the ref as typed but compare case-insensitively, so a differently
    // cased FS number can't slip past the dismissal.
    const next = existing.some(r => projectRefKey(r) === refKey)
      ? existing
      : [...existing, normaliseProjectRef(projectRef)];
    const { error } = await supabase.from('debrief_actions')
      .update({ dismissed_on_project_refs: next })
      .eq('id', action.id);
    setDismissing(null);
    if (!error) setCandidates(prev => prev.filter(c => c.id !== action.id));
  };

  if (hidden || candidates.length === 0) return null;

  const plain = (html) => String(html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

  return (
    <div className={`rounded-xl border border-[#2E8FA1]/30 bg-[#2E8FA1]/[0.05] p-4 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`${FONT} text-[13px] font-bold text-ink`}>
            From previous debriefs
          </p>
          <p className={`${FONT} text-[12px] text-[#4C4E42] mt-0.5`}>
            {candidates.length === 1
              ? 'One open action from an earlier project looks relevant here.'
              : `${candidates.length} open actions from earlier projects look relevant here.`}
            {' '}Nothing is required — this is a prompt, not a check.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setHidden(true)}
          className={`${FONT} text-[11px] font-semibold text-[#51534A] hover:text-ink flex-shrink-0 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fusion-forest`}>
          Hide
        </button>
      </div>
      <ul className="mt-3 space-y-2">
        {candidates.map(a => (
          <li key={a.id} className="bg-white border border-[#DDE6E8] rounded-lg px-3 py-2.5 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <p className={`${FONT} text-[13px] text-ink leading-snug`}>{plain(a.note_body) || 'Action'}</p>
              <p className={`${FONT} text-[11px] text-[#51534A] mt-0.5`}>
                {[a.owner || 'No owner', a.timeframe, a.topic_title, a.project_ref].filter(Boolean).join(' · ')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(a)}
              disabled={dismissing === a.id}
              // Says exactly what it does: the action is NOT closed, it just
              // stops being suggested here.
              title="Stop suggesting this on this project. The action stays open."
              className={`${FONT} text-[11px] font-semibold text-[#2E8FA1] hover:underline flex-shrink-0 disabled:opacity-50 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#2E8FA1]`}>
              {dismissing === a.id ? 'Dismissing…' : 'Not this project'}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
